import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Search, Gauge, Zap } from 'lucide-react';

const DiagnosticsPage = () => {
  const diagnosticServices = [
    {
      icon: Search,
      title: 'Electronic Leak Detection',
      description: 'Using sensitive electronic detectors to locate refrigerant leaks that may not be visible to the eye.',
    },
    {
      icon: Gauge,
      title: 'Pressure Testing',
      description: 'System pressure analysis to verify proper operation and identify potential issues.',
    },
    {
      icon: Zap,
      title: 'Electrical Testing',
      description: 'Checking compressor clutch, fans, and control circuits for proper function.',
    },
  ];

  const process = [
    'Visual inspection of all AC components and hoses',
    'Electronic leak detection across the entire system',
    'Pressure testing to check system integrity',
    'Temperature differential testing',
    'Electrical system checks',
    'Clear explanation of findings and recommendations',
  ];

  return (
    <div data-testid="diagnostics-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">
              Diagnostic Services
            </span>
            <h1 className="text-4xl font-bold text-slate-900 mt-3 mb-6">
              Diagnostics & Leak Testing
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Accurate diagnosis is the foundation of effective repair. We use professional equipment to identify exactly what's wrong with your air conditioning system.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
                data-testid="diagnostics-cta-book"
              >
                Book a Diagnostic Check
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:01323643333"
                className="inline-flex items-center gap-2 bg-white text-slate-700 px-6 py-3 rounded-lg font-medium border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <Phone className="w-4 h-4" />
                01323 643333
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Diagnostic Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">
            Our Diagnostic Services
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {diagnosticServices.map((service) => (
              <div key={service.title} className="text-center p-6">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <service.icon className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Our Diagnostic Process
              </h2>
              <p className="text-slate-600 mb-6">
                A thorough diagnostic check covers all aspects of your air conditioning system. Here's what we look at:
              </p>
              <ul className="space-y-3">
                {process.map((step, index) => (
                  <li key={step} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-slate-700">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80"
                alt="Diagnostic equipment in use"
                className="rounded-xl shadow-lg w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Diagnose First */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Why Diagnose First?
            </h2>
            <p className="text-slate-600 mb-8">
              Many air conditioning problems share similar symptoms but have very different causes. A proper diagnosis ensures we fix the actual problem, not just the symptoms. This saves you money and prevents repeat visits.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="p-4">
                <div className="text-3xl font-bold text-teal-600 mb-2">Save</div>
                <div className="text-slate-600 text-sm">Money on unnecessary parts</div>
              </div>
              <div className="p-4">
                <div className="text-3xl font-bold text-teal-600 mb-2">Avoid</div>
                <div className="text-slate-600 text-sm">Repeat visits for the same issue</div>
              </div>
              <div className="p-4">
                <div className="text-3xl font-bold text-teal-600 mb-2">Know</div>
                <div className="text-slate-600 text-sm">Exactly what needs fixing</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Get a Proper Diagnosis</h2>
          <p className="text-slate-300 mb-8">
            Stop guessing. Let us find out exactly what's wrong with your air conditioning.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
            data-testid="diagnostics-cta-bottom"
          >
            Request a Diagnostic Check
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DiagnosticsPage;
